import { Observable, filter, map, of, pairwise, startWith, switchMap, take } from 'rxjs';

import { Injectable, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';

import { AuthFacade, RouteFacade, UserPermission } from '@mitsubishi/core';
import { ExpiringMarkup, PriceListService } from '@mitsubishi/pricing-list/data-access';
import { DialogService } from '@mitsubishi/ui/dialog';

import {
  EXPIRING_MARKUP_GO_TO_WPL,
  ExpiringMarkupBlockingDialogComponent,
  ExpiringMarkupBlockingDialogResult,
} from './components/expiring-markup-blocking-dialog/expiring-markup-blocking-dialog.component';
import { ExpiringMarkupInfoDialogComponent } from './components/expiring-markup-info-dialog/expiring-markup-info-dialog.component';
import { WPL_ROUTE } from './expiring-markup.constants';
import { getBlockingExpiringMarkups, isWplUrl } from './expiring-markup.utils';

const HOME_ROUTE = '/';

@Injectable({ providedIn: 'root' })
export class ExpiringMarkupAlertService {
  readonly #priceListService = inject(PriceListService);
  readonly #dialogService = inject(DialogService);
  readonly #routeFacade = inject(RouteFacade);
  readonly #authFacade = inject(AuthFacade);
  readonly #router = inject(Router);

  // Populated when a non-blocking alert should be rendered inline (home page).
  // Empty when a dialog is used instead or there is nothing to show.
  readonly nonBlockingMarkups = signal<ExpiringMarkup[]>([]);

  #blockingDialogRef?: MatDialogRef<ExpiringMarkupBlockingDialogComponent, ExpiringMarkupBlockingDialogResult>;

  checkAndShow(): void {
    // Permissions land in the store on ngxsAfterBootstrap, which fires after
    // AppComponent init — so wait for the user profile before reading them.
    // The initial navigation is not blocking: until it ends router.url is still '/',
    // so a direct load of the WPL would pass the isWplUrl check and get the dialog.
    this.#authFacade.userInfo$
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.#initialNavigationEnd())
      )
      .subscribe(() => {
        if (this.#routeFacade.data['isPublicRoute'] === true) {
          return;
        }
        if (!this.#authFacade.hasPermission(UserPermission.ManageMarkup)) {
          return;
        }

        // The blocking dialog reads the raw drafts: postponing it must not depend on
        // (nor consume) the daily alert-read flag that filters get_expiring_markups.
        this.#getBlockingDrafts().subscribe(blocking => this.#showAlert(blocking));
        this.#watchWplExit();
      });
  }

  #initialNavigationEnd(): Observable<unknown> {
    return this.#router.navigated
      ? of(true)
      : this.#router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          take(1)
        );
  }

  dismissBannerAlert(): void {
    this.#priceListService.markMarkupAlertRead().subscribe();
    this.nonBlockingMarkups.set([]);
  }

  #showAlert(blocking: ExpiringMarkup[]): void {
    if (blocking.length) {
      this.#openBlockingDialog(blocking);
      return;
    }

    this.#priceListService.getExpiringMarkups().subscribe(list => this.#showNonBlockingAlert(list));
  }

  #showNonBlockingAlert(list: ExpiringMarkup[]): void {
    if (!list.length) {
      return;
    }

    if (this.#router.url.split(/[?#]/)[0] === HOME_ROUTE) {
      // Home renders an inline banner instead; alert-read fires on banner dismiss.
      this.nonBlockingMarkups.set(list);
      return;
    }

    this.#priceListService.markMarkupAlertRead().subscribe();
    this.#dialogService.openDialog(ExpiringMarkupInfoDialogComponent, 'md', { count: list.length });
  }

  // Re-checks on every WPL -> outside transition. startWith covers the first exit
  // after a direct load inside the WPL, whose NavigationEnd fired before we subscribed.
  #watchWplExit(): void {
    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects),
        startWith(this.#router.url),
        pairwise(),
        filter(([previous, current]) => isWplUrl(previous) && !isWplUrl(current)),
        switchMap(() => this.#getBlockingDrafts())
      )
      .subscribe(blocking => {
        if (blocking.length) {
          this.#openBlockingDialog(blocking);
        }
      });
  }

  #getBlockingDrafts(): Observable<ExpiringMarkup[]> {
    return this.#priceListService.getDraftMarkups().pipe(map(list => getBlockingExpiringMarkups(list)));
  }

  // Checked at open time, not request time: the user may be back in the WPL by
  // the time the drafts arrive. Inside the WPL the dialog waits for the next exit.
  #openBlockingDialog(rows: ExpiringMarkup[]): void {
    if (this.#blockingDialogRef || isWplUrl(this.#router.url)) {
      return;
    }

    this.#blockingDialogRef = this.#dialogService.openNotClosableDialog(ExpiringMarkupBlockingDialogComponent, 'xl', {
      rows,
    });
    this.#blockingDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        this.#blockingDialogRef = undefined;
        if (result === EXPIRING_MARKUP_GO_TO_WPL) {
          this.#router.navigate([WPL_ROUTE]);
        }
      });
  }
}
